import { useEffect, useState } from 'react';
import { SearchCandidateVectorQueryMessage } from 'modules/candidate-profile/types/SearchCandidateVectorQueryMessage';
import { useGetCandidateProfileLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { extractUuidsFromText } from 'core/utils/extractUuidsFromText';
import { CandidateProfileLinkData } from 'modules/candidate-profile/utils/parseMessageWithCandidatesAndChips';
import { getCandidateProfileDisplayName } from 'modules/candidate-profile/utils/getCandidateProfileDisplayName';

type UseConversationCandidateCacheProps = {
  conversationHistory: SearchCandidateVectorQueryMessage[];
};

/**
 * Manages a cache of candidate data for rendering links in conversation messages.
 * Fetches candidates referenced in conversation history by UUID.
 */
export const useConversationCandidateCache = ({
  conversationHistory
}: UseConversationCandidateCacheProps) => {
  const [conversationCandidates, setConversationCandidateCache] = useState<
    CandidateProfileLinkData[]
  >([]);

  const [getCandidateProfile] = useGetCandidateProfileLazyQuery();

  // Fetch missing candidate profiles referenced in conversation history
  useEffect(() => {
    const fetchMissingCandidates = async () => {
      if (conversationHistory.length === 0) return;

      // Extract all UUIDs from conversation messages
      const allUuids = new Set<string>();
      conversationHistory.forEach((message) => {
        const uuids = extractUuidsFromText(message.body);
        uuids.forEach((uuid) => allUuids.add(uuid));
      });
      const referencedUuids = Array.from(allUuids);

      // Find UUIDs that are not in the cache
      const missingUuids = referencedUuids.filter(
        (uuid) =>
          !conversationCandidates.some((candidate) => candidate.id === uuid)
      );

      if (missingUuids.length === 0) return;

      // Fetch missing candidates
      const fetchPromises = missingUuids.map((uuid) =>
        getCandidateProfile({ variables: { id: uuid } })
      );

      const results = await Promise.allSettled(fetchPromises);

      // Update conversation cache with fetched candidates
      const newCache = [...conversationCandidates];
      results.forEach((result) => {
        if (
          result.status === 'fulfilled' &&
          result.value.data?.getCandidateProfile
        ) {
          const candidateProfile = result.value.data.getCandidateProfile;
          newCache.push({
            id: candidateProfile.id,
            // No service writes the `name` scalar on its own.
            full_name: getCandidateProfileDisplayName(candidateProfile)
          });
        }
      });

      setConversationCandidateCache(newCache);
    };

    fetchMissingCandidates();
  }, [conversationHistory, getCandidateProfile]);

  const clearCache = () => {
    setConversationCandidateCache([]);
  };

  return {
    conversationCandidates,
    clearCache
  };
};
